import { useState, useMemo, useCallback } from "react";
import { Box, Grid, Flex, Text, TextField, Select } from "@radix-ui/themes";
import { useFiles } from "@/hooks/useFiles";
import { File, Folder } from "@/types/breeze/File";
import { downloadSecureFile, isPreviewable, formatFileSize, formatDimensions, formatDuration, UPLOADABLE_MEDIA_CATEGORIES, MEDIA_CATEGORY_LABELS, type MediaCategory } from "@/utils/fileUtils";
import { Search, FolderIcon, MoreHorizontalIcon, LayoutGrid, Rows3 } from "lucide-react";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { FileCard } from "@/components/files/FileCard";
import { DropdownMenu, IconButton } from "@radix-ui/themes";
import { useDebounce } from "@/hooks/useDebounce";
import { Checkbox } from "@radix-ui/themes";
import CustomPagination from "@/components/pagination/CustomPagination";
import { getFileTypeIcon } from "@/components/files/FileDisplay";

interface PaginatedFileGridProps {
  pageSize?: number;
  enableSelection?: boolean;
  showActions?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  selectedFiles?: File[];
  compact?: boolean;
  restrictedType?: "image" | "document" | undefined;
  onSelectionChanged?: (selectedFiles: File[]) => void;
  onSelectionLimitReached?: () => void;
  onDeleteFile?: (fileId: string) => void;
  onOpenGallery?: (index: number, files: File[]) => void;
  folderId?: number | null;
  /** Folders to render inline before files */
  folders?: Folder[];
  /** Called when a folder is clicked */
  onFolderClick?: (folderId: number) => void;
  /** Called when a folder action is triggered */
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
  onEditFile?: (file: File) => void;
  emptyState?: React.ReactNode;
}

export default function PaginatedFileGrid({
  pageSize = 25,
  enableSelection = false,
  showActions = true,
  multiple = true,
  maxFiles,
  selectedFiles = [],
  compact = false,
  restrictedType = undefined,
  onSelectionChanged,
  onSelectionLimitReached,
  onDeleteFile,
  onOpenGallery,
  folderId,
  folders = [],
  onFolderClick,
  onFolderRename,
  onFolderDelete,
  onEditFile,
  emptyState,
}: PaginatedFileGridProps) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState<Exclude<MediaCategory, "other"> | undefined>(restrictedType as Exclude<MediaCategory, "other"> | undefined);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<"original_file_name" | "created_at" | "updated_at" | "file_size">("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const debouncedSearch = useDebounce(search, 500);
  const { data: paginatedFiles, isLoading } = useFiles({
    page,
    pageSize: pageSizeState,
    search: debouncedSearch,
    fileType,
    folderId,
    sortBy,
    sortDirection,
  });

  const fileColumns: DataGridColumn<File>[] = [
    {
      field: "original_file_name",
      header: "Name",
      sortable: true,
      flex: 2,
      minWidth: 220,
      cellRenderer: (file) => {
        const { Icon, color } = getFileTypeIcon(file.file_type, file.original_file_name);
        return (
          <Flex align="center" gap="2" style={{ height: "100%" }}>
            <Icon size={16} color={color} style={{ flexShrink: 0 }} />
            <Text size="2" weight="medium" truncate>{file.original_file_name}</Text>
          </Flex>
        );
      },
      valueGetter: (file) => file.original_file_name,
    },
    {
      field: "file_type",
      header: "Type",
      sortable: true,
      minWidth: 140,
      flex: 1,
      cellRenderer: (file) => <Text size="1" color="gray">{file.file_type}</Text>,
    },
    {
      field: "file_size",
      header: "Size",
      sortable: true,
      minWidth: 90,
      flex: 0.6,
      cellRenderer: (file) => <Text size="1">{formatFileSize(file.file_size)}</Text>,
      valueGetter: (file) => String(file.file_size),
    },
    {
      field: "duration_seconds",
      header: "Duration",
      sortable: true,
      minWidth: 90,
      flex: 0.6,
      cellRenderer: (file) => <Text size="1">{formatDuration(file.duration_seconds) ?? "—"}</Text>,
    },
    {
      field: "width",
      header: "Dimensions",
      sortable: false,
      minWidth: 110,
      flex: 0.7,
      cellRenderer: (file) => <Text size="1">{formatDimensions(file.width, file.height) ?? "—"}</Text>,
    },
    {
      field: "is_public",
      header: "Access",
      sortable: true,
      minWidth: 90,
      flex: 0.5,
      cellRenderer: (file) => (
        <Text size="1" color={file.is_public ? "green" : "gray"}>
          {file.is_public ? "Public" : "Private"}
        </Text>
      ),
    },
    {
      field: "created_at",
      header: "Created",
      sortable: true,
      minWidth: 120,
      flex: 0.9,
      cellRenderer: (file) => <Text size="1">{new Date(file.created_at).toLocaleDateString()}</Text>,
    },
    {
      field: "updated_at",
      header: "Updated",
      sortable: true,
      minWidth: 120,
      flex: 0.9,
      cellRenderer: (file) => <Text size="1">{new Date(file.updated_at).toLocaleDateString()}</Text>,
    },
  ];

  const selectedFileIds = useMemo(
    () => new Set(selectedFiles.map((f) => f.id)),
    [selectedFiles],
  );

  const writeSelection = (updater: (current: File[]) => File[]) => {
    if (!enableSelection || !onSelectionChanged) return;
    onSelectionChanged(updater(selectedFiles));
  };

  const handleFileSelection = (file: File) => {
    if (!enableSelection) return;
    if (selectedFileIds.has(file.id)) {
      writeSelection((current) => current.filter((f) => f.id !== file.id));
      return;
    }
    if (!multiple) {
      writeSelection(() => [file]);
      return;
    }
    if (maxFiles && selectedFiles.length >= maxFiles) {
      onSelectionLimitReached?.();
      return;
    }
    writeSelection((current) => [...current, file]);
  };

  const allPageItemsSelected =
    paginatedFiles?.items &&
    paginatedFiles.items.length > 0 &&
    paginatedFiles.items.every((file) => selectedFileIds.has(file.id));

  const toggleSelectAll = () => {
    if (!paginatedFiles?.items) return;
    // Any existing selection (current page or cross-page) → clear everything.
    // Empty selection → select all on current page.
    if (selectedFiles.length > 0) {
      writeSelection(() => []);
      return;
    }
    const pageItems = paginatedFiles.items;
    writeSelection(() => {
      const additions: File[] = [];
      for (const file of pageItems) {
        if (maxFiles && additions.length >= maxFiles) {
          onSelectionLimitReached?.();
          break;
        }
        additions.push(file);
      }
      return additions;
    });
  };

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1); // Reset to first page when searching
  }, []);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: "16rem" }}>
        <Text>Loading files...</Text>
      </Flex>
    );
  }

  const gridColumns = compact
    ? {
        initial: "2",
        sm: "2",
        md: "3",
        lg: "3",
        xl: "3",
      }
    : {
        initial: "2",
        sm: "3",
        md: "4",
        lg: "5",
        xl: "5",
      };

  return (
    <Box>
      <Flex gap="4" mb="4" wrap="wrap">
        <Box style={{ flexGrow: 1 }}>
          <TextField.Root
            placeholder="Search files by name..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearch(e.target.value)
            }
          >
            <TextField.Slot>
              <Search className="h-4 w-4" />
            </TextField.Slot>
          </TextField.Root>
        </Box>

        {enableSelection && multiple && (
          <Flex align="center" gap="3" px="2">
            <Text
              as="label"
              size="2"
              color="gray"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <Checkbox
                checked={
                  selectedFiles.length > 0
                    ? allPageItemsSelected
                      ? true
                      : "indeterminate"
                    : false
                }
                onCheckedChange={toggleSelectAll}
              />
              {selectedFiles.length > 0 ? `Clear (${selectedFiles.length})` : "Select All"}
            </Text>
          </Flex>
        )}
        {!restrictedType && (
          <Select.Root
            value={fileType || "all"}
            onValueChange={(value) =>
              setFileType(value === "all" ? undefined : (value as Exclude<MediaCategory, "other">))
            }
          >
            <Select.Trigger />
            <Select.Content style={{ zIndex: 9999999 }}>
              <Select.Item value="all">All Files</Select.Item>
              {UPLOADABLE_MEDIA_CATEGORIES.map((cat) => (
                <Select.Item key={cat} value={cat}>
                  {MEDIA_CATEGORY_LABELS[cat]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        )}
        <Flex gap="1" align="center" style={{ border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-2)", padding: 2 }}>
          <IconButton size="2" variant={viewMode === "grid" ? "solid" : "ghost"} onClick={() => setViewMode("grid")} title="Grid view">
            <LayoutGrid size={16} />
          </IconButton>
          <IconButton size="2" variant={viewMode === "list" ? "solid" : "ghost"} onClick={() => setViewMode("list")} title="List view">
            <Rows3 size={16} />
          </IconButton>
        </Flex>
      </Flex>

      {!paginatedFiles?.items?.length && folders.length === 0 ? (
        emptyState || (
          <Flex align="center" justify="center" style={{ height: "16rem" }}>
            <Text color="gray">No files found</Text>
          </Flex>
        )
      ) : (
        <>
          {paginatedFiles && paginatedFiles.total_pages > 0 && (
          <Box mb="4">
            <CustomPagination
              currentPage={page}
              totalPages={paginatedFiles.total_pages}
              pageSize={pageSizeState}
              totalItems={paginatedFiles.total_items}
              pageSizeOptions={[10, 25, 50, 100, 250, 500]}
              onPageChange={setPage}
              onPageSizeChange={(newSize) => {
                setPageSizeState(newSize);
                setPage(1);
              }}
            />
          </Box>
          )}

          {viewMode === "list" ? (
            <Flex direction="column" gap="1">
              {folders.map((folder) => (
                <Flex
                  key={`folder-${folder.id}`}
                  align="center"
                  gap="3"
                  onClick={() => onFolderClick?.(folder.id)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "var(--radius-2)",
                    cursor: "pointer",
                    border: "1px solid var(--gray-a4)",
                    backgroundColor: "var(--color-panel-solid)",
                  }}
                >
                  <FolderIcon size={20} color="var(--accent-9)" fill="var(--accent-4)" strokeWidth={1.5} />
                  <Text size="2" weight="medium" style={{ flex: 1 }}>{folder.name}</Text>
                  <Text size="1" color="gray">
                    {folder.file_count > 0 ? `${folder.file_count} file${folder.file_count !== 1 ? "s" : ""}` : "Empty"}
                  </Text>
                  {(onFolderRename || onFolderDelete) && (
                    <Box onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                          <IconButton variant="ghost" size="1"><MoreHorizontalIcon size={14} /></IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content size="1">
                          {onFolderRename && <DropdownMenu.Item onClick={() => onFolderRename(folder)}>Rename</DropdownMenu.Item>}
                          {onFolderDelete && (
                            <>
                              <DropdownMenu.Separator />
                              <DropdownMenu.Item color="red" onClick={() => onFolderDelete(folder)}>Delete</DropdownMenu.Item>
                            </>
                          )}
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>
                    </Box>
                  )}
                </Flex>
              ))}
              <DataGrid
                data={paginatedFiles}
                columns={fileColumns}
                storageKey="files-list-grid"
                currentPage={page}
                currentPageSize={pageSizeState}
                rowSelection={enableSelection ? (multiple ? "multiple" : "single") : "none"}
                showPagination={false}
                selectedRowIds={selectedFiles.map((f) => f.id)}
                onPageChange={setPage}
                onPageSizeChange={(newSize) => { setPageSizeState(newSize); setPage(1); }}
                onSelectionChanged={(rows) => {
                  if (!onSelectionChanged) return;
                  const pageIds = new Set((paginatedFiles?.items ?? []).map((f) => f.id));
                  const merged = [
                    ...selectedFiles.filter((f) => !pageIds.has(f.id)),
                    ...rows,
                  ];
                  onSelectionChanged(merged);
                }}
                onSortChange={(field, dir) => {
                  if (field === "original_file_name" || field === "created_at" || field === "updated_at" || field === "file_size") {
                    setSortBy(field);
                    setSortDirection((dir as "asc" | "desc") || "asc");
                    setPage(1);
                  }
                }}
                onCellClicked={(_, file) => {
                  if (isPreviewable(file)) {
                    const index = (paginatedFiles?.items ?? []).findIndex((f) => f.id === file.id);
                    if (index >= 0) onOpenGallery?.(index, paginatedFiles?.items ?? []);
                  }
                }}
              />
            </Flex>
          ) : (
          <Grid columns={gridColumns} gap="3">
            {/* Folders first */}
            {folders.map((folder) => (
              <Box
                key={`folder-${folder.id}`}
                onClick={() => onFolderClick?.(folder.id)}
                style={{
                  border: "1px solid var(--gray-5)",
                  borderRadius: "var(--radius-3)",
                  padding: "16px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  position: "relative",
                  minHeight: compact ? 100 : 140,
                }}
              >
                <FolderIcon size={compact ? 28 : 36} color="var(--accent-9)" fill="var(--accent-4)" strokeWidth={1.5} />
                <Text size="2" weight="medium" align="center" truncate style={{ maxWidth: "100%" }}>{folder.name}</Text>
                <Text size="1" color="gray">
                  {folder.file_count > 0 ? `${folder.file_count} file${folder.file_count !== 1 ? "s" : ""}` : "Empty"}
                </Text>
                {(onFolderRename || onFolderDelete) && (
                  <Box style={{ position: "absolute", top: 6, right: 6 }} onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger>
                        <IconButton variant="ghost" size="1"><MoreHorizontalIcon size={14} /></IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content size="1">
                        {onFolderRename && <DropdownMenu.Item onClick={() => onFolderRename(folder)}>Rename</DropdownMenu.Item>}
                        {onFolderDelete && (
                          <>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Item color="red" onClick={() => onFolderDelete(folder)}>Delete</DropdownMenu.Item>
                          </>
                        )}
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  </Box>
                )}
              </Box>
            ))}

            {/* Then files */}
            {(paginatedFiles?.items || []).map((file, index) => {
              const isFileSelected = selectedFileIds.has(file.id);
              const isFileDisabled =
                !isFileSelected &&
                multiple &&
                !!maxFiles &&
                selectedFileIds.size >= maxFiles;

              return (
                <Box key={file.id}>
                  <FileCard
                    file={file}
                    onClick={async () => {
                      if (isPreviewable(file)) {
                        onOpenGallery?.(index, paginatedFiles?.items || []);
                      } else {
                        try {
                          await downloadSecureFile(
                            file.id,
                            file.original_file_name,
                          );
                        } catch (error) {
                          console.error("Failed to download file:", error);
                        }
                      }
                    }}
                    onDelete={
                      showActions && onDeleteFile
                        ? () => onDeleteFile(String(file.id))
                        : () => {}
                    }
                    onEdit={onEditFile ? () => onEditFile(file) : undefined}
                    isSelected={isFileSelected}
                    isDisabled={isFileDisabled}
                    onToggleSelect={() => handleFileSelection(file)}
                    compact={compact}
                    showActions={showActions}
                  />
                </Box>
              );
            })}
          </Grid>
          )}
        </>
      )}
    </Box>
  );
}
